import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import type { CarouselAPI } from '@elfsight/lib-carousel';
import {
  CarouselItem,
  LazyCarousel,
  MIN_BULLETS_COUNT
} from '@elfsight/lib-carousel';
import { generateRowsChunks } from '@elfsight/lib-utils';
import type { Swiper } from '@elfsight/reexported-swiper';
import {
  calculateAutoColumns,
  defaultRect,
  useIsMobile,
  useStableRect
} from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { concat } from 'es-toolkit/compat';
import type { ColumnsModes, DataSourceProps, MappingFunction } from '../types';
import {
  computeClassNames,
  createCarouselAPI,
  generateDataChunks,
  transformDataSource,
  useColumns,
  useDelayedLoopToggle,
  useLayoutData
} from '../utils';

export interface CarouselLayoutProps {
  dataSource: DataSourceProps;
  columnsMode: ColumnsModes;
  columnsDesktopLarge: number;
  columnsDesktopMedium: number;
  columnsDesktopSmall: number;
  columnsTablet: number;
  columnsMobile: number;
  rows: number;
  itemSpacing: number;
  scrollMode: 'slide' | 'item';
  rowsOnMobile: number;
  autoplay: boolean;
  pauseOnHover: boolean;
  autoplayInterval: number;
  animationType: 'slide' | 'coverflow';
  animationSpeed: number;
  pagination: boolean;
  arrowsEnable: boolean;
  arrowsShowOnHover: boolean;
  arrowsOnMobileEnable: boolean;
  swipeDesktopEnable: boolean;
  swipeMobileEnable: boolean;
  ariaLabel?: string;
  rtl?: boolean;
  columnsBasis?: number;
  arrowBackgroundColor?: string;
  arrowColor?: string;
  arrowBackgroundColorOnHover?: string;
  arrowColorOnHover?: string;
  arrowSize?: number;
  arrowMargin?: number;
  paginationColor?: string;
  className?: string;
  reportSlideChangeInstantly?: boolean;
  loop?: boolean;
  renderItem: MappingFunction;
  renderLoader: MappingFunction;
  containerHorizontalPadding?: number;
  onVisibleItemsChange?: (visibleItemsIndexes: number[]) => void;
  onColumnsChange?: (columns: number) => void;
  onRowsChange?: (rows: number) => void;
}

export const CarouselLayout = forwardRef<CarouselAPI, CarouselLayoutProps>(
  function CarouselLayout(
    {
      dataSource,
      columnsMode,
      columnsDesktopLarge,
      columnsDesktopMedium,
      columnsDesktopSmall,
      columnsTablet,
      columnsMobile,
      columnsBasis = 240,
      rows,
      rowsOnMobile,
      itemSpacing,
      scrollMode,
      autoplay,
      pauseOnHover,
      autoplayInterval,
      animationType,
      animationSpeed,
      pagination,
      arrowsEnable,
      arrowsOnMobileEnable,
      swipeDesktopEnable,
      swipeMobileEnable,
      ariaLabel,
      rtl,
      arrowsShowOnHover,
      arrowBackgroundColor,
      arrowColor,
      arrowColorOnHover,
      arrowBackgroundColorOnHover,
      arrowSize,
      arrowMargin,
      paginationColor,
      className,
      reportSlideChangeInstantly,
      loop = false,
      renderItem,
      renderLoader,
      containerHorizontalPadding,
      onVisibleItemsChange,
      onColumnsChange,
      onRowsChange
    },
    forwardingRef
  ) {
    const isDataStatic = Array.isArray(dataSource);
    const dataSourceFormat = transformDataSource(dataSource);

    const {
      data: initialData,
      hasNext: hasNextFetch,
      next: handleNextFetch
    } = dataSourceFormat;

    const carouselRefAPI = useRef<CarouselAPI>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement | null>(null);

    const [containerRect, setContainerRect] = useState(defaultRect);
    const [containerRectRef] = useStableRect(containerRect, setContainerRect);

    const containerMergedRef = useMergedRef(containerRef, containerRectRef);

    const columnOptions = {
      columnsDesktopLarge: columnsDesktopLarge || 1,
      columnsDesktopMedium: columnsDesktopMedium || 1,
      columnsDesktopSmall: columnsDesktopSmall || 1,
      columnsTablet: columnsTablet || 1,
      columnsMobile: columnsMobile || 1
    };

    const { windowWidth, customColumns, recalculateColumns } =
      useColumns(columnOptions);

    const computedColumns =
      columnsMode === 'auto'
        ? calculateAutoColumns({
            containerWidth: containerRect.width,
            columnsBasis,
            itemSpacing
          })
        : customColumns;

    const columnsCount = Math.min(computedColumns, initialData.length || 1);

    const normalizedAnimationSpeed =
      scrollMode === 'slide'
        ? animationSpeed
        : animationSpeed / (columnsCount || 1);

    const isMobile = useIsMobile();

    const swipeEnabled = useMemo(
      () => (isMobile ? swipeMobileEnable : swipeDesktopEnable),
      [isMobile, swipeDesktopEnable, swipeMobileEnable]
    );

    const arrowsEnabled = useMemo(
      () => (isMobile ? arrowsOnMobileEnable : arrowsEnable),
      [isMobile, arrowsEnable, arrowsOnMobileEnable]
    );
    const showArrowsControlOnHover = useMemo(
      () => (isMobile ? false : arrowsShowOnHover),
      [isMobile, arrowsShowOnHover]
    );

    const rowsCount = useMemo(
      () => (isMobile ? rowsOnMobile || 1 : rows),
      [isMobile, rows, rowsOnMobile]
    );

    const [normalizedColumnsCount, normalizedRowsCount] = useMemo(
      () => [columnsCount, rowsCount].map((count) => Math.ceil(count) || 1),
      [columnsCount, rowsCount]
    );

    const itemsPerPage = useMemo(
      () => normalizedColumnsCount * normalizedRowsCount,
      [normalizedColumnsCount, normalizedRowsCount]
    );

    const {
      data,
      hasNext,
      loadNext,
      reset: resetLayout
    } = useLayoutData({
      data: initialData,
      itemsCountPerPage: Math.max(itemsPerPage * 2, MIN_BULLETS_COUNT),
      hasNextFetch,
      handleNextFetch,
      withPreloadData: true,
      isDataStatic
    });

    const shouldLoop = useMemo(() => {
      if (isDataStatic) {
        return loop && data.length > itemsPerPage;
      }

      return hasNext !== undefined && !hasNext && data.length > itemsPerPage;
    }, [isDataStatic, loop, hasNext, data, itemsPerPage]);
    const isLoopActive = useDelayedLoopToggle(shouldLoop, animationSpeed);

    const [currentChunk, setChunk] = useState(0);

    const [dataChunks, setDataChunks] = useState(
      generateDataChunks(
        data,
        itemsPerPage,
        currentChunk,
        renderItem,
        renderLoader,
        isLoopActive
      )
    );

    const dataForRender = concat(...dataChunks);

    const dataRowsChunks = generateRowsChunks(
      dataForRender,
      normalizedColumnsCount,
      normalizedRowsCount,
      itemSpacing
    );

    const handleSlideChange = (swiper: Swiper) => {
      const slideIndex = swiper.realIndex;
      const slidesCount = swiper.slides.length;
      const slidesPerView =
        swiper.params.slidesPerView === 'auto'
          ? normalizedColumnsCount
          : Math.ceil(swiper.params.slidesPerView || 1);

      if (
        slidesCount > slidesPerView &&
        slideIndex === slidesCount - slidesPerView * 2
      ) {
        loadNext();
      }

      const chunk = Math.round(slideIndex / slidesPerView);
      if (chunk !== currentChunk) {
        setChunk(chunk);
      }
    };

    useEffect(() => {
      const switchAutoplayHandler: IntersectionObserverCallback = ([entry]) => {
        const carouselApi = carouselRefAPI.current;

        if (!carouselApi || !entry) {
          return;
        }

        const isCarouselVisible = entry.isIntersecting;
        const isCarouselRunning = carouselApi.autoplayIsRunning();

        if (autoplay && isCarouselRunning && !isCarouselVisible) {
          carouselApi.autoplayStop();
        }
        if (autoplay && !isCarouselRunning && isCarouselVisible) {
          carouselApi.autoplayStart();
        }
        // В момент включения isLoopActive происходит замирание автоплея,
        // чтобы его устранить происходит искусственное переключение слайда
        if (autoplay && isLoopActive && isCarouselVisible) {
          setTimeout(() => {
            carouselApi.slideToNext();
          }, autoplayInterval);
        }
      };
      const observer = new IntersectionObserver(switchAutoplayHandler);
      const carouselWrapper = containerRef.current;

      if (carouselWrapper) {
        observer.observe(carouselWrapper);
      }

      return () => {
        if (carouselWrapper) {
          observer.unobserve(carouselWrapper);
        }
      };
    }, [isLoopActive, autoplay, containerRef, carouselRefAPI]);

    const updateAutoHeight = useCallback(() => {
      if (carouselRefAPI?.current) {
        carouselRefAPI.current.updateAutoHeight();
      }
    }, []);

    useEffect(() => {
      onColumnsChange?.(normalizedColumnsCount);
    }, [normalizedColumnsCount]);

    useEffect(() => {
      onRowsChange?.(normalizedRowsCount);
    }, [normalizedRowsCount]);

    useEffect(() => {
      setDataChunks(
        generateDataChunks(
          data,
          itemsPerPage,
          currentChunk,
          renderItem,
          renderLoader,
          isLoopActive
        )
      );
      updateAutoHeight();
    }, [
      data,
      itemsPerPage,
      currentChunk,
      renderItem,
      renderLoader,
      isLoopActive
    ]);

    const resetCarouselSlide = useCallback(() => {
      recalculateColumns();
      setChunk(0);
      if (carouselRefAPI?.current) {
        carouselRefAPI.current.slideTo(0);
      }
    }, [recalculateColumns]);

    useImperativeHandle(
      forwardingRef,
      () =>
        createCarouselAPI(carouselRefAPI, { resetLayout: resetCarouselSlide }),
      [resetCarouselSlide]
    );

    useEffect(() => {
      resetCarouselSlide();
    }, [
      resetCarouselSlide,
      columnsMode,
      columnsDesktopLarge,
      columnsDesktopMedium,
      columnsDesktopSmall,
      columnsTablet,
      columnsMobile,
      windowWidth,
      animationType,
      itemsPerPage
    ]);

    useEffect(() => {
      if (isDataStatic) {
        resetCarouselSlide();
      }
    }, [initialData]);

    useEffect(() => {
      resetLayout();
    }, [rtl]);

    return (
      <CarouselContainer
        ref={containerMergedRef}
        className={computeClassNames('es-carousel-layout-container', className)}
      >
        <CarouselWrapper
          ref={measureRef}
          className="es-carousel-layout-wrapper"
        >
          <LazyCarousel
            ariaLabel={ariaLabel}
            ref={carouselRefAPI}
            withAutoplay={autoplay}
            autoplayDelay={autoplayInterval}
            slideBy={scrollMode}
            gap={itemSpacing}
            loop={isLoopActive}
            itemsPerSlide={columnsCount}
            withArrowControl={arrowsEnabled}
            showArrowsControlOnHover={showArrowsControlOnHover}
            withPaginationControl={pagination}
            rtl={rtl}
            pauseOnHover={pauseOnHover}
            allowTouchMove={swipeEnabled}
            effect={animationType}
            animationSpeed={normalizedAnimationSpeed}
            onSlideChange={handleSlideChange}
            arrowControlBackgroundColor={arrowBackgroundColor}
            arrowControlBackgroundColorOnHover={arrowBackgroundColorOnHover}
            arrowControlIconColor={arrowColor}
            arrowControlIconColorOnHover={arrowColorOnHover}
            arrowControlSize={arrowSize}
            arrowControlMargin={arrowMargin}
            paginationControlBackgroundColor={paginationColor}
            bulletInsideGap={500}
            className="es-carousel-layout"
            arrowControlPosition="adaptive"
            onVisibleItemsChange={onVisibleItemsChange}
            reportSlideChangeInstantly={reportSlideChangeInstantly}
            containerHorizontalPadding={containerHorizontalPadding}
          >
            {dataRowsChunks?.map((item, index) => (
              <CarouselItem key={index} className="es-carousel-layout-item">
                {item}
              </CarouselItem>
            ))}
          </LazyCarousel>
        </CarouselWrapper>
      </CarouselContainer>
    );
  }
);

export default CarouselLayout;

const CarouselWrapper = styled.div`
  width: 100%;
`;

const CarouselContainer = styled.div`
  max-width: 100%;
  /* Необходимо для исключения краша carousel layout при width: 100%, если родительскому контейнеру задан display: flex */
  display: grid;
  grid-template-columns: minmax(0, 1fr);
`;
